import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ChevronLeft, Search, ScrollText, Loader2, RefreshCw, Download } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { LOG_TYPES, EXECUTION_STATUSES } from './automationConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutomationLogsViewer = ({ automationId, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [logTypeFilter, setLogTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [automationId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, execRes] = await Promise.all([
        axios.get(`${API}/automation-logs`, {
          params: automationId ? { automation_id: automationId } : {}
        }),
        axios.get(`${API}/automation-executions`, {
          params: automationId ? { automation_id: automationId } : {}
        })
      ]);
      setLogs(logsRes.data);
      setExecutions(execRes.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (logTypeFilter !== 'all' && log.log_type !== logTypeFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return log.message?.toLowerCase().includes(query) ||
             log.automation_id?.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ScrollText className="h-6 w-6" />
              {automationId ? 'Automation Logs' : 'All Logs & Executions'}
            </h1>
            <p className="text-sm text-muted-foreground">View execution history and audit trail</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setActiveTab('logs')}
          className={cn(
            "pb-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            activeTab === 'logs'
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Logs ({logs.length})
        </button>
        <button
          onClick={() => setActiveTab('executions')}
          className={cn(
            "pb-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            activeTab === 'executions'
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Executions ({executions.length})
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {activeTab === 'logs' && (
          <Select value={logTypeFilter} onValueChange={setLogTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Log Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(LOG_TYPES).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeTab === 'logs' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Automation</TableHead>
                  <TableHead>Contact/Job</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map(log => {
                    const typeConfig = LOG_TYPES[log.log_type] || { label: log.log_type, color: 'text-slate-600' };
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {log.timestamp ? format(new Date(log.timestamp), 'MMM d, h:mm:ss a') : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate">{log.message}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.automation_id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.contact_id && `C: ${log.contact_id.slice(0, 8)}`}
                          {log.job_id && ` J: ${log.job_id.slice(0, 8)}`}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Automation</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No executions found
                    </TableCell>
                  </TableRow>
                ) : (
                  executions.map(exec => {
                    const statusConfig = EXECUTION_STATUSES[exec.status] || EXECUTION_STATUSES.queued;
                    return (
                      <TableRow key={exec.id}>
                        <TableCell className="text-sm">
                          {exec.started_at ? format(new Date(exec.started_at), 'MMM d, h:mm a') : 'Pending'}
                        </TableCell>
                        <TableCell className="font-medium">{exec.automation_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{exec.trigger_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell>{exec.steps?.length || 0} steps</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {exec.started_at && exec.completed_at
                            ? `${Math.round((new Date(exec.completed_at) - new Date(exec.started_at)) / 1000)}s`
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
